import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useMutation } from '@tanstack/react-query'
import {
  Button,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  InputLeftAddon,
  InputGroup,
  useToast,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter
} from '@chakra-ui/react'
import { RentalApplicationFee } from '@homevest/types/rental-applications'

import axios from 'lib/axios'

import { formatMoney } from 'lib/numbers'

export type NewFeeAmounts = {
  rentalApplicationFeeId: string
  newFeeAmount: string
}[]

const EditRentalApplicationFeeModal: React.FC<
  React.PropsWithChildren<{
    isOpen: boolean
    close: () => void
    rentalApplicationFees: (RentalApplicationFee & { user_name: string })[]
    onRefresh?: () => void | Promise<void>
  }>
> = ({ isOpen, close, rentalApplicationFees, onRefresh }) => {
  const toast = useToast()
  const [newFeeAmounts, setNewFeeAmounts] = useState<NewFeeAmounts>([])

  const { mutate: editRentalApplicationFees } = useMutation({
    mutationFn: async (data: NewFeeAmounts) => {
      return axios.put('/admin/rental_application_fees/expected_amount', {
        newFeeAmounts: data
      })
    },
    onSuccess: async () => {
      toast({
        title: 'Rental Application Fees Updated',
        status: 'success',
        duration: 5000
      })
      close()
      if (onRefresh) {
        await onRefresh()
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Error Updating Rental Application Fees',
        description: error?.message || 'An unknown error occurred',
        status: 'error',
        duration: 5000
      })
    }
  })

  const handleAmountChange = (feeId: string, value: string) => {
    setNewFeeAmounts((prev) => {
      const existingIndex = prev.findIndex((fee) => fee.rentalApplicationFeeId === feeId)
      if (existingIndex >= 0) {
        const updated = [...prev]
        updated[existingIndex] = {
          rentalApplicationFeeId: feeId,
          newFeeAmount: value
        }
        return updated
      }
      return [...prev, { rentalApplicationFeeId: feeId, newFeeAmount: value }]
    })
  }

  const isAnyInputBlank = () => {
    return newFeeAmounts.some((fee) => fee.newFeeAmount === '')
  }

  return (
    <Modal isOpen={isOpen} onClose={close} size='xl'>
      <ModalContent>
        <ModalHeader>Edit Rental Application Fees</ModalHeader>
        <ModalBody>
          <TableContainer>
            <Table variant='simple'>
              <Thead>
                <Tr>
                  <Th w='45%'>Name</Th>
                  <Th w='20%'>Current Fee</Th>
                  <Th w='35%'>New Fee</Th>
                </Tr>
              </Thead>
              <Tbody>
                {rentalApplicationFees.map((fee) => (
                  <Tr key={fee.user_id}>
                    <Td>{fee.user_name}</Td>
                    <Td>{formatMoney(fee.expected_amount, 2, '$')}</Td>
                    <Td>
                      <InputGroup>
                        <InputLeftAddon>$</InputLeftAddon>
                        <Input
                          type='number'
                          min={0}
                          step='0.01'
                          isInvalid={
                            newFeeAmounts.find((f) => f.rentalApplicationFeeId === fee.id)
                              ?.newFeeAmount === ''
                          }
                          value={
                            newFeeAmounts.find((f) => f.rentalApplicationFeeId === fee.id)
                              ?.newFeeAmount ?? fee.expected_amount
                          }
                          onChange={(e) => {
                            const value = e.target.value
                            handleAmountChange(fee.id, value)
                          }}
                        />
                      </InputGroup>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </ModalBody>
        <ModalFooter>
          <Button
            size='xs'
            colorScheme='teal'
            onClick={() => editRentalApplicationFees(newFeeAmounts)}
            isDisabled={isAnyInputBlank()}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

EditRentalApplicationFeeModal.propTypes = {
  close: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  rentalApplicationFees: PropTypes.array.isRequired
}

export default EditRentalApplicationFeeModal
